import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import Table from 'apollo-react/components/Table';
import moment from 'moment';
import React from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { clearTopMenuModal, closeTopMenuModal } from '../../actions/TopMenuActions';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import {
  getFilterComponentByDataType,
  getFilterFunctionByDataType,
  getSortFunction,
} from '../../form-designer/utils/controls/ssGrid.utils';
import { getInstalledVersions } from '../../selectors/InstalledVersionsSelectors';
import { safeFormatDateString } from '../../utils/dateUtils';

const useStyles = makeStyles({
  content: {
    width: 1000,
    minHeight: 150,
  },
  modal: {
    maxWidth: 'unset',
  },
  table: {
    border: '1px solid #e9e9e9',
  },
});

const RenderDate = ({ row, column: { accessor } }) => {
  const rowValue = row[accessor];
  const date = rowValue && moment(rowValue).isValid() ? safeFormatDateString(rowValue) : rowValue;

  return <>{date}</>;
};

const InstalledVersionsDialogComponent = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const translateString = useTranslation();
  const { isMobileDevice } = useWindowDimensions();

  // Starts fading out the modal
  const handleModalClose = () => {
    batch(() => {
      dispatch(closeTopMenuModal());
      dispatch(clearTopMenuModal());
    });
  };

  const { isOpen, columns, rows } = useSelector(getInstalledVersions);

  const parsedColumns = React.useMemo(
    () =>
      columns.map((column) => ({
        header: column.Header,
        accessor: column.Accessor,
        sortFunction: getSortFunction(column.DataType),
        filterFunction: getFilterFunctionByDataType({
          dataType: column.DataType,
          accessor: column.Accessor,
        }),
        filterComponent: getFilterComponentByDataType({ dataType: column.DataType }),
        customCell: column.DataType === 'System.DateTime' ? RenderDate : undefined,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen]
  );

  const parsedRows = React.useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        MinRequiredVersion: row.MinimumRequiredVersion,
        CreatedDate: (row.CreatedDate && moment.utc(row.CreatedDate).local()) || '',
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen]
  );

  const open = isOpen;
  const isLoading = isOpen && !columns.length;
  const hasError = false;

  const title = isLoading ? '' : translateString('Installed Versions');
  const variant = hasError ? 'error' : 'default';

  return (
    <Modal
      hideButtons
      className={classes.modal}
      disableBackdropClick
      disableEscapeKeyDown
      disablePortal
      onClose={() => handleModalClose()}
      open={open}
      title={title}
      variant={variant}
      PaperProps={{ style: { margin: isMobileDevice ? 16 : 32 } }}
    >
      <div className={classes.content}>
        {isLoading ? (
          <Loader isInner />
        ) : (
          <div className={classes.table}>
            <Table
              columns={parsedColumns}
              rows={parsedRows}
              rowsPerPageOptions={[10, 25, 50]}
              showFilters
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InstalledVersionsDialogComponent;
